import { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ConfidenceInput (spec §7.3, §9.3 step 4) — a 5-point self-assessment that is
 * captured BEFORE the resolution is shown.
 *
 * The ordering is the product promise, not a layout detail: calibration is the
 * gap between how sure someone was and how right they were, and asking after
 * the reveal would measure hindsight instead. The runner therefore never holds
 * the resolution while this control is on screen — it is fetched by
 * `POST …/complete`, which the backend refuses until a confidence exists
 * (`confidence_required`).
 *
 * Built on native radio inputs so roving focus, arrow keys, `Home`/`End`, form
 * semantics and mobile assistive tech all behave without re-implementation.
 */

/**
 * @param {object} props
 * @param {number|null} [props.value] currently selected 1..scaleMax.
 * @param {(value: number) => void} props.onChange
 * @param {string} [props.prompt] authored question; falls back to the generic step copy.
 * @param {number} [props.scaleMax=5]
 * @param {boolean} [props.disabled]
 * @param {import('react').ReactNode} [props.error] validation message.
 * @param {string} [props.className]
 */
export const ConfidenceInput = forwardRef(function ConfidenceInput(
  { value = null, onChange, prompt, scaleMax = 5, disabled = false, error, className = '', ...rest },
  ref
) {
  const { t } = useTranslation();
  const groupId = useId();
  const hintId = `${groupId}-hint`;
  const errorId = `${groupId}-error`;
  const steps = Array.from({ length: Math.max(2, scaleMax) }, (_, index) => index + 1);

  return (
    <fieldset
      ref={ref}
      className={`etch-confidence ${className}`.trim()}
      aria-describedby={error ? `${errorId} ${hintId}` : hintId}
      aria-invalid={error ? 'true' : undefined}
      aria-required="true"
      disabled={disabled}
      {...rest}
    >
      <legend className="etch-confidence__legend">{prompt || t('missions.step.confidenceHelp')}</legend>

      <div className="etch-confidence__scale" data-steps={steps.length}>
        {steps.map((step) => {
          const optionId = `${groupId}-${step}`;
          const label = t([`missions.confidenceScale.${step}`, 'missions.step.confidence']);
          return (
            <label key={step} className="etch-confidence__option" htmlFor={optionId}>
              <input
                type="radio"
                id={optionId}
                name={groupId}
                value={step}
                checked={value === step}
                onChange={() => onChange?.(step)}
              />
              <span className="etch-confidence__value" aria-hidden="true">
                {step}
              </span>
              <span className="etch-confidence__label">{label}</span>
            </label>
          );
        })}
      </div>

      <p className="etch-confidence__hint" id={hintId}>
        {t('missions.confidenceLockedHint')}
      </p>
      {error && (
        <p className="etch-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
});

export default ConfidenceInput;
